import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  ArrowRight, ArrowLeft, Check, HeartPulse, TestTube, Apple, CalendarDays, SkipForward,
  Sparkles, Pill, PartyPopper, Crown, Lock, Rocket,
} from "lucide-react";
import { toast } from "sonner";
import BloodTests from "@/components/nutri/BloodTests";
import FoodPreferences from "@/components/nutri/FoodPreferences";
import WeeklyDiet from "@/components/nutri/WeeklyDiet";
import Supplements from "@/components/nutri/Supplements";
import { UpsellBanner } from "@/components/PremiumLock";

const STEPS = [
  { title: "Anamnesi", icon: HeartPulse },
  { title: "Analisi", icon: TestTube },
  { title: "Alimenti", icon: Apple },
  { title: "Integratori", icon: Pill },
  { title: "Dieta", icon: CalendarDays },
  { title: "Fine", icon: PartyPopper },
];
const SUBS = [
  { title: "Dati personali", hint: "Iniziamo con qualche dato di base su di te." },
  { title: "Stile di vita", hint: "Raccontaci la tua giornata tipo e il movimento." },
  { title: "Obiettivo", hint: "Qual è il traguardo che vuoi raggiungere?" },
  { title: "Anamnesi clinica", hint: "Serve a DietFlix per creare una dieta sicura per te." },
];
const HINTS = [
  null,
  "Carica il PDF delle analisi: DietFlix lo legge per te. È facoltativo, puoi farlo anche più tardi.",
  "Scegli gli alimenti che ami: DietFlix li userà per costruire i tuoi pasti.",
  "DietFlix individua gli integratori più adatti al tuo profilo e li abbina ai pasti.",
  "Ci siamo! DietFlix unisce anamnesi, analisi e gusti per creare la tua dieta.",
  "Il tuo percorso è pronto. Benvenuto in DietFlix!",
];

const init = {
  age: "", sex: "uomo", height_cm: "", weight_kg: "", target_weight_kg: "",
  activity_level: "moderato", typical_day: "", sport_habits: "", sport_frequency: "",
  goal: "perdita_peso", dietary_style: "onnivoro",
  pathologies: "", medical_devices: "", medications: "", allergies: "", intolerances: "",
};

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-2 ${className}`}><Label>{label}</Label>{children}</div>
);

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

function AnamnesiSub({ sub, form, set }) {
  const num = (k, tid) => <Input type="number" inputMode="decimal" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={tid} />;
  if (sub === 0) return (
    <div className="grid grid-cols-2 gap-4 stagger">
      <Field label="Età">{num("age", "onb-age")}</Field>
      <Field label="Sesso"><Pick value={form.sex} onChange={(v) => set("sex", v)} testid="onb-sex" options={[["uomo", "Uomo"], ["donna", "Donna"]]} /></Field>
      <Field label="Altezza (cm)">{num("height_cm", "onb-height")}</Field>
      <Field label="Peso attuale (kg)">{num("weight_kg", "onb-weight")}</Field>
      <Field label="Peso obiettivo (kg)" className="col-span-2">{num("target_weight_kg", "onb-target-weight")}</Field>
    </div>
  );
  if (sub === 1) return (
    <div className="space-y-4 stagger">
      <Field label="Livello di attività quotidiana">
        <Pick value={form.activity_level} onChange={(v) => set("activity_level", v)} testid="onb-activity" options={[
          ["sedentario", "Sedentario (ufficio, poco movimento)"], ["leggero", "Leggero (1-3 allenamenti/sett.)"],
          ["moderato", "Moderato (3-5 allenamenti/sett.)"], ["attivo", "Attivo (6-7 allenamenti/sett.)"],
          ["molto_attivo", "Molto attivo (lavoro fisico/atleta)"]]} />
      </Field>
      <Field label="Com'è la tua giornata tipo?">
        <Textarea value={form.typical_day} onChange={(e) => set("typical_day", e.target.value)} placeholder="Orari dei pasti, lavoro, sonno, tempo per cucinare…" data-testid="onb-typical-day" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Abitudini sportive" className="col-span-2 sm:col-span-1">
          <Input value={form.sport_habits} onChange={(e) => set("sport_habits", e.target.value)} placeholder="es. Palestra, corsa, nuoto" data-testid="onb-sport" />
        </Field>
        <Field label="Frequenza (volte/settimana)" className="col-span-2 sm:col-span-1">{num("sport_frequency", "onb-sport-freq")}</Field>
      </div>
    </div>
  );
  if (sub === 2) return (
    <div className="space-y-4 stagger">
      <Field label="Obiettivo principale">
        <Pick value={form.goal} onChange={(v) => set("goal", v)} testid="onb-goal" options={[
          ["perdita_peso", "Perdita di peso"], ["ricomposizione", "Ricomposizione corporea"], ["tonificazione", "Tonificazione"],
          ["mantenimento", "Mantenimento"], ["massa", "Aumento massa muscolare"]]} />
      </Field>
      <Field label="Stile alimentare">
        <Pick value={form.dietary_style} onChange={(v) => set("dietary_style", v)} testid="onb-diet-style" options={[
          ["onnivoro", "Onnivoro"], ["vegetariano", "Vegetariano"], ["vegano", "Vegano"], ["pescetariano", "Pescetariano"], ["mediterraneo", "Mediterraneo"]]} />
      </Field>
    </div>
  );
  return (
    <div className="space-y-4 stagger">
      <p className="text-sm text-muted-foreground">Queste informazioni servono a evitare interazioni con farmaci o dispositivi medici. Lascia vuoto ciò che non ti riguarda.</p>
      <Field label="Patologie diagnosticate">
        <Textarea value={form.pathologies} onChange={(e) => set("pathologies", e.target.value)} placeholder="es. Diabete tipo 2, ipertensione…" data-testid="onb-pathologies" />
      </Field>
      <Field label="Dispositivi medici">
        <Input value={form.medical_devices} onChange={(e) => set("medical_devices", e.target.value)} placeholder="es. Microinfusore, pacemaker…" data-testid="onb-devices" />
      </Field>
      <Field label="Farmaci in uso">
        <Textarea value={form.medications} onChange={(e) => set("medications", e.target.value)} placeholder="es. Metformina, statine…" data-testid="onb-medications" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Allergie" className="col-span-2 sm:col-span-1"><Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} data-testid="onb-allergies" /></Field>
        <Field label="Intolleranze" className="col-span-2 sm:col-span-1"><Input value={form.intolerances} onChange={(e) => set("intolerances", e.target.value)} data-testid="onb-intolerances" /></Field>
      </div>
    </div>
  );
}

function Stepper({ phase }) {
  return (
    <div className="flex items-center gap-1 mb-6 overflow-x-auto scrollbar-none pb-1" data-testid="onb-stepper">
      {STEPS.map(({ icon: Ic, title }, i) => (
        <div key={title} className="flex items-center gap-1 shrink-0">
          <div className="flex flex-col items-center gap-1 w-14 sm:w-16">
            <div className={`h-10 w-10 rounded-2xl grid place-items-center transition-all duration-300 ${
              i < phase ? "bg-primary text-primary-foreground"
                : i === phase ? "bg-brand text-white shadow-lg shadow-purple-500/30 scale-110 pulse-ring"
                : "bg-secondary text-muted-foreground"}`} data-testid={`onb-dot-${i}`}>
              {i < phase ? <Check className="h-4 w-4" /> : <Ic className="h-4 w-4" />}
            </div>
            <span className={`text-[11px] font-medium ${i === phase ? "text-foreground" : "text-muted-foreground"}`}>{title}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`h-0.5 w-3 sm:w-6 rounded mb-5 transition-colors ${i < phase ? "bg-primary" : "bg-secondary"}`} />}
        </div>
      ))}
    </div>
  );
}

function FinalStep({ premium }) {
  const perks = [
    ["Dieta su misura", premium ? "7 giorni su 7" : "Lunedì completo", !premium],
    ["Alimenti preferiti", premium ? "Illimitati + esclusioni" : "Fino a 3", !premium],
    ["Integratori", premium ? "Protocollo completo" : "1 consiglio", !premium],
    ["Ricette e lista della spesa", premium ? "Incluse" : "Solo Premium", !premium],
    ["Peso, acqua e analisi del sangue", "Inclusi", false],
  ];
  return (
    <div className="space-y-5" data-testid="onb-final-step">
      <div className="text-center space-y-2 py-2">
        <div className="h-16 w-16 rounded-3xl bg-brand grid place-items-center mx-auto shadow-lg pop-in"><Rocket className="h-8 w-8 text-white" /></div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold">Il tuo percorso è pronto!</h2>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">Abbiamo registrato anche il tuo peso di partenza: da oggi DietFlix ti accompagna giorno per giorno.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 stagger">
        {perks.map(([t, v, locked]) => (
          <div key={t} className={`rounded-2xl border p-4 flex items-center justify-between gap-3 card-lift ${locked ? "bg-secondary/40" : "bg-card"}`}>
            <div>
              <div className="text-sm font-medium">{t}</div>
              <div className="text-xs text-muted-foreground">{v}</div>
            </div>
            {locked ? <Lock className="h-4 w-4 text-accent" /> : <Check className="h-4 w-4 text-primary" />}
          </div>
        ))}
      </div>
      {!premium && (
        <UpsellBanner testid="onb-final-upsell" title="Sblocca tutta la tua dieta"
          text="7 giorni su misura, ricette passo-passo, lista della spesa e preferiti illimitati." cta="Passa a Premium" />
      )}
    </div>
  );
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const STORAGE_KEY = `dietflix_onb2_${user?.user_id || "anon"}`;
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
  })();
  const [phase, setPhase] = useState(Number.isInteger(saved?.phase) ? saved.phase : 0);
  const [sub, setSub] = useState(Number.isInteger(saved?.sub) ? saved.sub : 0);
  const [form, setForm] = useState(saved?.form ? { ...init, ...saved.form } : init);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const premium = !!user?.is_premium;

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ phase, sub, form })); } catch {}
  }, [phase, sub, form, STORAGE_KEY]);

  const step0Ok = () => form.age && form.height_cm && form.weight_kg && form.target_weight_kg;

  const saveProfile = async () => {
    if (!step0Ok()) { toast.error("Completa i dati personali"); setSub(0); return; }
    setBusy(true);
    try {
      await api.put("/profile", {
        ...form,
        age: parseInt(form.age, 10),
        height_cm: parseFloat(form.height_cm),
        weight_kg: parseFloat(form.weight_kg),
        target_weight_kg: parseFloat(form.target_weight_kg),
        sport_frequency: parseInt(form.sport_frequency || "0", 10),
      });
      await refreshUser();
      toast.success("Anamnesi salvata! Continuiamo insieme.");
      setPhase(1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const next = () => {
    if (phase === 0) {
      if (sub === 0 && !step0Ok()) { toast.error("Compila i campi richiesti"); return; }
      if (sub < SUBS.length - 1) { setSub(sub + 1); return; }
      saveProfile();
      return;
    }
    if (phase === STEPS.length - 1) {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      navigate("/dashboard");
      return;
    }
    setPhase(phase + 1);
  };

  const back = () => {
    if (phase === 0) { setSub(Math.max(0, sub - 1)); return; }
    if (phase === 1) { setPhase(0); setSub(SUBS.length - 1); return; }
    setPhase(phase - 1);
  };

  const hint = phase === 0 ? SUBS[sub].hint : HINTS[phase];
  const wide = phase >= 1 && phase <= 4;
  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="min-h-screen bg-background nutri-grain">
      <div className={`${wide ? "max-w-4xl" : "max-w-2xl"} mx-auto px-4 py-8 sm:py-12 transition-all`}>
        <div className="rounded-3xl bg-brand text-white p-6 sm:p-8 mb-6 shadow-xl relative overflow-hidden" data-testid="onb-hero">
          <div className="hero-orb h-40 w-40 bg-amber-300 -right-10 -top-14" />
          <div className="hero-orb h-32 w-32 bg-cyan-300 right-28 -bottom-16" style={{ animationDelay: "3s" }} />
          <div className="relative">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="inline-flex bg-white rounded-xl px-3 py-1.5 shadow"><img src="/DietFlix-logo.webp" alt="DietFlix" className="h-6" /></div>
              {!premium && <span className="text-[11px] font-semibold uppercase tracking-wide rounded-full bg-white/20 px-3 py-1" data-testid="onb-plan-badge">Piano gratuito</span>}
              {premium && <span className="text-[11px] font-semibold uppercase tracking-wide rounded-full bg-white/20 px-3 py-1 inline-flex items-center gap-1"><Crown className="h-3 w-3" /> Premium</span>}
            </div>
            <h1 className="font-heading text-2xl sm:text-4xl font-semibold leading-tight" data-testid="onb-greeting">
              Ciao {firstName}! Costruiamo la tua dieta.
            </h1>
            <p className="text-white/85 text-sm sm:text-base mt-2 max-w-lg">{hint}</p>
          </div>
        </div>

        <Stepper phase={phase} />

        <div key={`${phase}-${sub}`} className="bg-card border rounded-3xl p-5 sm:p-8 shadow-sm space-y-5 pop-in" data-testid="onboarding-card">
          {phase === 0 && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="font-heading text-xl font-semibold" data-testid="onb-sub-title">{SUBS[sub].title}</div>
                <span className="text-xs font-mono-data text-muted-foreground" data-testid="onb-sub-counter">Passo {sub + 1} di {SUBS.length}</span>
              </div>
              <Progress value={((sub + 1) / SUBS.length) * 100} className="h-1.5" />
              <AnamnesiSub sub={sub} form={form} set={set} />
            </>
          )}
          {phase === 1 && <div data-testid="onb-blood-step"><BloodTests /></div>}
          {phase === 2 && <div data-testid="onb-foods-step"><FoodPreferences /></div>}
          {phase === 3 && <div data-testid="onb-supplements-step"><Supplements /></div>}
          {phase === 4 && <div data-testid="onb-diet-step"><WeeklyDiet /></div>}
          {phase === 5 && <FinalStep premium={premium} />}

          <div className="flex justify-between items-center pt-4 border-t mt-2 gap-2">
            <Button variant="ghost" onClick={back} disabled={(phase === 0 && sub === 0) || busy} data-testid="onb-back-button">
              <ArrowLeft className="h-4 w-4 mr-2" /> Indietro
            </Button>
            <div className="flex gap-2">
              {phase >= 1 && phase <= 4 && (
                <Button variant="outline" onClick={() => setPhase(phase + 1)} data-testid="onb-skip-button">
                  <SkipForward className="h-4 w-4 mr-2" /> Salta
                </Button>
              )}
              {phase === 0 && sub < SUBS.length - 1 && (
                <Button onClick={next} className="btn-brand border-0 rounded-full px-6" data-testid="onb-next-button">
                  Avanti <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              {phase === 0 && sub === SUBS.length - 1 && (
                <Button onClick={next} disabled={busy} className="btn-brand border-0 rounded-full px-6" data-testid="onb-save-profile-button">
                  <Check className="h-4 w-4 mr-2" /> {busy ? "Salvataggio…" : "Salva e continua"}
                </Button>
              )}
              {phase >= 1 && phase <= 4 && (
                <Button onClick={next} className="btn-brand border-0 rounded-full px-6" data-testid="onb-continue-button">
                  Continua <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              {phase === 5 && (
                <Button onClick={next} className="btn-brand border-0 rounded-full px-6" data-testid="onb-finish-button">
                  <Sparkles className="h-4 w-4 mr-2" /> Vai alla dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
